import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import {
  INFO_TEXT_WIDGET_DISPLAY_ON_ADDITIONAL_FIELDS,
  InputsGeneratorComponent,
} from '../inputs-generator/inputs-generator.component';
import { OrderformSettingsStateCustomForms } from '@orderform/widgets';
import { combineLatest, Subject } from 'rxjs';

@Component({
  selector: 'orderform-input-additional',
  templateUrl: './input-additional.component.html',
  styleUrls: ['./input-additional.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputAdditionalComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  infoText = INFO_TEXT_WIDGET_DISPLAY_ON_ADDITIONAL_FIELDS;
  PGB_VIEW_MODE = isPageBuilderDesignMode();
  order$ = this._orderFormFascade.order$;
  settings$ = this._orderFormFascade.settings$;

  filledCustomForms$ = this.order$.pipe(
    map((order) => order.order.custom_forms),
    shareReplay(1)
  );

  customForms$ = this.settings$.pipe(
    map((settings) =>
      settings.custom_forms.filter((form) => form.show_on === 'form')
    ),
    shareReplay(1)
  );

  private _mergedMultipleCustomFormData = {};
  private _afterViewInit$ = new Subject<void>();

  @ViewChildren('fieldInput') fieldInput: QueryList<InputsGeneratorComponent>;
  @Output() readonly validate = new EventEmitter<{
    valid: boolean;
    source: 'change' | 'submit';
  }>();

  private _formValidationSubscription = this._afterViewInit$
    .pipe(
      switchMap(() =>
        this.fieldInput.changes.pipe(
          startWith(null),
          switchMap(() =>
            combineLatest(
              this.fieldInput.map((c) =>
                c.stateChange.pipe(
                  startWith(null),
                  map(() => c.isValid())
                )
              )
            )
          ),
          map((result) => {
            return result.reduce((validAll, valid) => {
              return validAll && valid;
            }, true);
          })
        )
      )
    )
    .subscribe((valid) => {
      this.validate.emit({ valid, source: 'change' });
    });

  private _submitFailedSubscription =
    this._orderFormFascade.submitFailed.subscribe(() => {
      const valid = this.fieldInput.reduce((allCustomFormsValid, input) => {
        return allCustomFormsValid && input.isValid();
      }, true);
      this.validate.emit({ valid, source: 'submit' });
    });

  constructor(private _orderFormFascade: OrderformFascade) {}

  setData(value) {
    this._mergedMultipleCustomFormData = {
      ...this._mergedMultipleCustomFormData,
      ...value,
    };
    return this._orderFormFascade.setOrderStore({
      custom_forms: this._mergedMultipleCustomFormData,
    });
  }

  ngOnInit(): void {}

  public trackByCustomFormId(
    index: number,
    form: OrderformSettingsStateCustomForms.RootObject
  ) {
    return form.no;
  }

  ngOnDestroy() {
    this._formValidationSubscription.unsubscribe();
    this._submitFailedSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    this._afterViewInit$.next();
  }
}
